/**
 * v1.1 — Smart Auto-Release "notify" mode confirmation dialog.
 *
 * Opens when the user taps a push notification with event=auto_release_confirm.
 * Lets them Release (calls /sessions/auto-release/:id/confirm) or Keep (calls
 * /dismiss). Designed to be lightweight — no router involvement, just modal
 * state held in App-level useState driven by useAutoReleaseConfirm().
 */
import { useState } from 'react';
import { useTranslation } from '../i18n';
import { confirmAutoRelease, dismissAutoRelease } from '../api/users';
import { analytics } from '../hooks/useAnalytics';

interface Props {
  open:      boolean;
  releaseId: string | null;
  onClose:   () => void;
}

export default function ConfirmAutoReleaseDialog({ open, releaseId, onClose }: Props) {
  const { t, direction } = useTranslation();
  const [busy,    setBusy]    = useState(false);
  const [error,   setError]   = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  if (!open || !releaseId) return null;

  async function handleRelease() {
    if (!releaseId) return;
    setBusy(true);
    setError(null);
    try {
      await confirmAutoRelease(releaseId);
      analytics.trackAutoReleaseConfirmReleased();
      setSuccess(true);
      // Auto-close after a brief success state so the user sees confirmation.
      setTimeout(() => { onClose(); resetLocal(); }, 1800);
    } catch (err: any) {
      const code = err?.response?.data?.error?.code;
      if (code === 'NOT_FOUND' || err?.response?.status === 404) {
        setError(t('auto_release_confirm_expired'));
      } else {
        setError(t('auto_release_confirm_error'));
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleDismiss() {
    if (!releaseId) return;
    setBusy(true);
    analytics.trackAutoReleaseConfirmDismissed();
    try {
      await dismissAutoRelease(releaseId);
    } catch {
      // Dismiss is best-effort — if it errors, the intent will expire on its own.
    } finally {
      onClose();
      resetLocal();
    }
  }

  function resetLocal() {
    setBusy(false);
    setError(null);
    setSuccess(false);
  }

  return (
    <div style={overlay} role="dialog" aria-modal="true" dir={direction}>
      <div style={card}>
        <div style={{ fontSize: '40px', textAlign: 'center', marginBottom: '12px' }}>🅿️</div>
        <h2 style={title}>{t('auto_release_confirm_title')}</h2>
        <p style={body}>
          {success ? t('auto_release_confirm_done') : t('auto_release_confirm_body')}
        </p>

        {error && (
          <div style={errorBox}>{error}</div>
        )}

        {!success && (
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '16px' }}>
            <button
              onClick={handleDismiss}
              disabled={busy}
              style={{ ...btn, background: '#f3f4f6', color: '#374151' }}
            >
              {t('auto_release_confirm_dismiss')}
            </button>
            <button
              onClick={handleRelease}
              disabled={busy}
              style={{ ...btn, background: '#16a34a', color: '#fff' }}
            >
              {busy ? '…' : t('auto_release_confirm_release')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const overlay: React.CSSProperties = {
  position:       'fixed',
  inset:          0,
  background:     'rgba(17,24,39,0.45)',
  display:        'flex',
  alignItems:     'center',
  justifyContent: 'center',
  padding:        '20px',
  zIndex:         9999,
};
const card: React.CSSProperties = {
  background:    '#fff',
  borderRadius:  '14px',
  padding:       '20px',
  maxWidth:      '380px',
  width:         '100%',
  boxShadow:     '0 12px 32px -4px rgba(0,0,0,0.15)',
};
const title: React.CSSProperties = {
  fontSize:   '18px',
  fontWeight: 700,
  color:      '#111',
  margin:     '0 0 8px',
  textAlign:  'center',
};
const body: React.CSSProperties = {
  fontSize:   '13px',
  color:      '#374151',
  lineHeight: 1.5,
  margin:     0,
  textAlign:  'center',
};
const btn: React.CSSProperties = {
  padding:      '10px 16px',
  border:       'none',
  borderRadius: '8px',
  fontSize:     '14px',
  fontWeight:   600,
  cursor:       'pointer',
};
const errorBox: React.CSSProperties = {
  marginTop:    '12px',
  padding:      '8px 10px',
  background:   '#fee2e2',
  color:        '#991b1b',
  borderRadius: '8px',
  fontSize:     '12px',
};
